import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  Outlet,
} from "react-router-dom";

import { Provider, useDispatch, useSelector } from "react-redux";
import { ThemeProvider } from "@mui/material/styles";
import { CssBaseline } from "@mui/material";
import { HelmetProvider } from "react-helmet-async";

import LoginPage from "./pages/LoginPage";
import SignUpForm from "./pages/SignUpForm";
import NadraOnboardingForm from "./pages/NadraOnboardingForm";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import EmailVerificationSuccessPage from "./pages/EmailVerificationSuccessPage";

import { GoogleOAuthProvider } from "@react-oauth/google";
import { PublicClientApplication, EventType } from "@azure/msal-browser";
import { msalConfig } from "./config/msAuthConfig";
import { MsalProvider } from "@azure/msal-react";

import { deinitializeGA, initializeGA, trackPageView } from "./analytics";

import { PersistGate } from "redux-persist/integration/react";
import { persistor, store } from "./store";

import App from "./App";

import HomePage from "./pages/HomePage";
import Bookmarks from "./pages/Bookmarks";
import LegalLibrary from "./components/LegalLibrary";

import ApplicationDrafting from "./components/ApplicationDrafting";
import PetitionDrafting from "./components/PetitionDrafting";
import ContractDrafting from "./components/PetitionDrafting/ContractDrafting";

import CaseManagmentSystem from "./pages/CaseManagmentSystem/index";
import CmsSharedDocumentPage from "./pages/CmsSharedDocumentPage";
import CaseManagement from "./pages/CaseManagement/index";

import SubscriptionPlans from "./pages/Subscriptions";
import McpConnector from "./pages/McpConnector";
import { TutorialVideos } from "./pages/Tutorial Videos";

import { LegalChat } from "./pages/LegalChat";
import { CaseSearch } from "./pages/CaseSearch";
import { StatuteSearchParent } from "./pages/StatuteSearch";
import { DocumentAnalysisParent } from "./pages/DocumentAnalysis";
import { JudgeGPTParent } from "./pages/JudgeGPT";

import ContractAnalysis from "./pages/ContractAnalysis/ContractAnalysis";
import ChatRoutePage from "./pages/RagChat/ChatRoutePage";

import ComingSoon from "./components/CaseManagement/Components/ComingSoon";

import { ErrorBoundaryWithKey } from "./components/ErrorBoundary";

import InvitationResponse from "./pages/CaseManagmentSystem/InvitationResponse";

import { USER_INFO_KEY } from "./utils/HelperFunction/Constants";
import { setEncryptedItem } from "./utils/storageUtils";

import { theme } from "./config/muiTheme";

import ProtectedModuleRoute from "./routes/ProtectedModuleRoute";

import "./index.css";

import SignUpRequestsManagement from "./pages/SignUpRequestsManagement";
const root = ReactDOM.createRoot(document.getElementById("root"));

initializeGA();

store.subscribe(() => {
  const userLogin = store.getState().userLogin;
  const { userInfo } = userLogin;

  if (userInfo && userInfo._id) {
    initializeGA();
  } else {
    deinitializeGA();
  }
});

const msalInstance = new PublicClientApplication(msalConfig);

msalInstance.initialize();

const activeAccount = msalInstance.getActiveAccount();

if (!activeAccount) {
  const accounts = msalInstance.getAllAccounts();

  if (accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0]);
  }
}

msalInstance.addEventCallback((event) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
    const authenticationResult = event.payload;

    const account = authenticationResult.account;

    msalInstance.setActiveAccount(account);
  }
});

msalInstance.enableAccountStorageEvents();

const TrackPageViews = ({ children }) => {
  const location = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  useEffect(() => {
    const handleResponse = async (response) => {
      if (response && response.account) {
        const { name, username } = response.account;

        try {
          const res = await fetch(
            `https://rg-law-gpt-be-auth2.azurewebsites.net/api/createUserWithGoogle`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                name,
                email: username,
              }),
            },
          );

          const data = await res.json();

          setEncryptedItem(USER_INFO_KEY, data);

          dispatch({
            type: "USER_LOGIN_SUCCESS",
            payload: data,
          });

          // NOTE:
          // This <Navigate /> does not actually navigate because
          // it is not being returned/rendered.
          // Keeping existing behavior here.
        } catch (error) {
          console.log("Failed to register user:", error);
        }
      }
    };

    msalInstance.handleRedirectPromise().then(handleResponse);

    // Check if the user is already signed in
    const account = msalInstance.getActiveAccount();

    if (account) {
      handleResponse({ account });
    }
  }, [dispatch]);

  return children;
};

initializeGA();

if (process.env.REACT_APP_ENV === "production") {
  document.addEventListener("contextmenu", (e) => e.preventDefault());

  document.addEventListener("keydown", (e) => {
    if (
      e.key === "F12" ||
      (e.ctrlKey &&
        e.shiftKey &&
        ["I", "J", "C", "K"].includes(e.key.toUpperCase())) ||
      (e.ctrlKey && e.key.toUpperCase() === "U")
    ) {
      e.preventDefault();
    }
  });
}

window.addEventListener("error", (e) => {
  if (
    e.message === "ResizeObserver loop completed with undelivered notifications"
  ) {
    e.stopImmediatePropagation();
  }
});

const AdminOnlyRoute = () => {
  const userInfo = useSelector((state) => state.userLogin?.userInfo);

  if (!userInfo) {
    return <Navigate to='/' replace />;
  }

  if (userInfo.role?.toLowerCase() !== "admin") {
    return <Navigate to='/app/home' replace />;
  }

  return <Outlet />;
};

root.render(
  <HelmetProvider>
    <ThemeProvider theme={theme}>
      <CssBaseline />

      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <MsalProvider instance={msalInstance}>
            <GoogleOAuthProvider clientId='168527565999-ku8psofqjgsf673vh38ekf94rns19sv2.apps.googleusercontent.com'>
              <React.StrictMode>
                <Router>
                  <TrackPageViews>
                    <ErrorBoundaryWithKey>
                      <Routes>
                        {/* =================================================
                            PUBLIC ROUTES
                            ================================================= */}

                        <Route path='/' element={<LoginPage />} />

                        <Route path='/signup' element={<SignUpForm />} />

                        <Route
                          path='/nadra-onboarding'
                          element={<NadraOnboardingForm />}
                        />

                        <Route
                          path='/forgot-password'
                          element={<ForgotPassword />}
                        />

                        <Route
                          path='/reset-password/:token'
                          element={<ResetPasswordPage />}
                        />

                        <Route
                          path='/verify-email/:token'
                          element={<EmailVerificationSuccessPage />}
                        />

                        <Route
                          path='/join-as-a-lawyer/:token'
                          element={<InvitationResponse />}
                        />

                        <Route path='/app' element={<App />}>
                          <Route
                            index
                            element={<Navigate to='home' replace />}
                          />

                          <Route path='home' element={<HomePage />} />

                          <Route
                            element={
                              <ProtectedModuleRoute requiredModule='Legal Chat' />
                            }
                          >
                            <Route path='legal-chat' element={<LegalChat />} />
                          </Route>

                          <Route
                            element={
                              <ProtectedModuleRoute requiredModule='Case Search' />
                            }
                          >
                            <Route
                              path='searchResults'
                              element={<CaseSearch />}
                            />

                            <Route
                              path='searchResults/:caseId'
                              element={<CaseSearch />}
                            />
                          </Route>

                          <Route
                            element={
                              <ProtectedModuleRoute requiredModule='Statute Search' />
                            }
                          >
                            <Route
                              path='statute-search'
                              element={<StatuteSearchParent />}
                            />

                            <Route
                              path='statute-search/:statuteName'
                              element={<StatuteSearchParent />}
                            />
                          </Route>

                          <Route
                            element={
                              <ProtectedModuleRoute requiredModule='Application Drafting' />
                            }
                          >
                            <Route
                              path='application-drafting'
                              element={<ApplicationDrafting />}
                            />

                            <Route
                              path='application-drafting/:documentId'
                              element={<ApplicationDrafting />}
                            />
                          </Route>

                          <Route
                            element={
                              <ProtectedModuleRoute requiredModule='Petition Drafting' />
                            }
                          >
                            <Route
                              path='petition-drafting'
                              element={<PetitionDrafting />}
                            />

                            <Route
                              path='petition-drafting/:documentId'
                              element={<PetitionDrafting />}
                            />
                          </Route>

                          <Route
                            element={
                              <ProtectedModuleRoute requiredModule='Contract Drafting' />
                            }
                          >
                            <Route
                              path='contract-drafting'
                              element={<ContractDrafting />}
                            />

                            <Route
                              path='contract-drafting/:documentId'
                              element={<ContractDrafting />}
                            />
                          </Route>

                          <Route
                            element={
                              <ProtectedModuleRoute requiredModule='Bookmarks' />
                            }
                          >
                            <Route path='bookmarks' element={<Bookmarks />} />
                          </Route>

                          <Route
                            element={
                              <ProtectedModuleRoute requiredModule='Document Analysis' />
                            }
                          >
                            <Route
                              path='document-analysis'
                              element={<DocumentAnalysisParent />}
                            />
                          </Route>

                          <Route
                            element={
                              <ProtectedModuleRoute requiredModule='Legal Library' />
                            }
                          >
                            <Route
                              path='legal-library'
                              element={<LegalLibrary />}
                            />
                          </Route>

                          <Route
                            element={
                              <ProtectedModuleRoute requiredModule='Case Management System' />
                            }
                          >
                            <Route
                              path='case-management'
                              element={<CaseManagmentSystem />}
                            />
                            <Route
                              path='cms-document/:caseId/:docId'
                              element={<CmsSharedDocumentPage />}
                            />
                          </Route>

                          <Route
                            element={
                              <ProtectedModuleRoute requiredModule='Case Management' />
                            }
                          >
                            <Route
                              path='cms-document/:caseId/:docId'
                              element={<CmsSharedDocumentPage />}
                            />
                          </Route>

                          <Route
                            element={
                              <ProtectedModuleRoute requiredModule='JudgeGPT' />
                            }
                          >
                            <Route path='judge-gpt' element={<ComingSoon />} />
                          </Route>

                          <Route
                            path='subscriptions'
                            element={<SubscriptionPlans />}
                          />
                          <Route
                            path='mcp-connector'
                            element={<McpConnector />}
                          />
                          <Route element={<AdminOnlyRoute />}>
                            <Route
                              path='signup-requests'
                              element={<SignUpRequestsManagement />}
                            />
                          </Route>

                          <Route
                            path='tutorial-videos'
                            element={<TutorialVideos />}
                          />

                          <Route
                            element={
                              <ProtectedModuleRoute requiredModule='Contract Analysis' />
                            }
                          >
                            <Route
                              path='contract-analysis'
                              element={<ContractAnalysis />}
                            />
                          </Route>

                          <Route
                            element={
                              <ProtectedModuleRoute requiredModule='Document Chat' />
                            }
                          >
                            <Route
                              path='document-chat'
                              element={<ChatRoutePage />}
                            />

                            <Route
                              path='document-chat/:chatId'
                              element={<ChatRoutePage />}
                            />
                          </Route>
                        </Route>

                        <Route path='*' element={<Navigate to='/' replace />} />
                      </Routes>
                    </ErrorBoundaryWithKey>
                  </TrackPageViews>
                </Router>
              </React.StrictMode>
            </GoogleOAuthProvider>
          </MsalProvider>
        </PersistGate>
      </Provider>
    </ThemeProvider>
  </HelmetProvider>,
);
